const colors = ["#2138CC", "#001560", "#B8B8B8", "#B0BAFF"];

// Function to generate a random color
function generateRandomColor() {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

export function groupAndTransform(data, key) {
  const amountMap = data.reduce((acc, curr) => {
    const amount = parseFloat(curr.allocationUSD); // Ensure the allocationUSD is treated as a number

    // Check if the key corresponds to an array of values (e.g., Sector)
    if (Array.isArray(curr[key])) {
      // Handle each value in the array (each Sector)
      curr[key].forEach((subKey) => {
        acc[subKey] = (acc[subKey] || 0) + amount;
      });
    } else {
      // Handle as a single value (not an array)
      acc[curr[key]] = (acc[curr[key]] || 0) + amount;
    }

    return acc;
  }, {});

  const totalAmount = Object.values(amountMap).reduce(
    (sum, amount) => sum + amount,
    0
  );

  let colorIndex = 0;
  // Assign colors to each key, ensuring no duplicates within the same dataset
  const colorAssignment = {};
  Object.keys(amountMap).forEach((name) => {
    if (colorIndex < colors.length) {
      colorAssignment[name] = colors[colorIndex++];
    } else {
      let newColor;
      do {
        newColor = generateRandomColor();
      } while (Object.values(colorAssignment).includes(newColor));
      colorAssignment[name] = newColor;
    }
  });

  // Transform to desired structure, applying color assignment
  return Object.entries(amountMap).map(([name, amount]) => ({
    value: (amount / totalAmount) * 100,
    name,
    itemStyle: { color: colorAssignment[name] },
  }));
}

export function getTiers(mainData, investment) {
  const matchingInvestments = mainData.filter(
    (record) => investment.name === record.Deals
  );
  const { allocationUSD } = investment;
  const tiers = matchingInvestments.filter((record) => {
    const lowerBound = record["Lower Bound"];
    const upperBound = record["Upper Bound"];
    return allocationUSD >= lowerBound && allocationUSD < upperBound;
  });
  return {
    investment,
    tiers,
  };
}

export function getCFs(investmentTiers) {
  const result = {};

  for (const record of investmentTiers) {
    const { investment, tiers } = record;

    if (!tiers.length) continue;

    if (
      !result[investment.name] &&
      !result[investment.name]?.calls &&
      !result[investment.name]?.distributions &&
      !result[investment.name]?.netEM
    ) {
      result[investment.name] = {
        calls: [],
        distributions: [],
        netEM: 0,
      };
    }

    const allocationUSD = Number(investment.allocationUSD);

    let calls, distributions;
    for (const tier of tiers) {
      if (tier.Capital === "Calls") calls = tier;
      if (tier.Capital === "Distributions") distributions = tier;
    }

    if (calls) {
      const callsCopy = { ...calls };
      for (const callKey of Object.keys(calls)) {
        if (callKey.startsWith("Dec")) {
          const dec = calls[callKey];
          callsCopy[callKey] = dec * allocationUSD;
        }
      }
      result[investment.name].calls = callsCopy;
    }

    if (distributions) {
      const distributionsCopy = { ...distributions };
      for (const distributionKey of Object.keys(distributions)) {
        if (distributionKey.startsWith("Dec")) {
          const dec = distributions[distributionKey];
          distributionsCopy[distributionKey] = dec * allocationUSD;
        }
      }
      result[investment.name].distributions = distributionsCopy;
      result[investment.name].netEM = aggregateCallsAndDistributions([
        result[investment.name],
      ]).netEM;
    }
  }

  return result;
}

export function aggregateCallsAndDistributions(cfs) {
  // Initialize data structures to aggregate data
  const aggregatedCalls = {};
  const aggregatedDistributions = {};
  const aggregatedNetCashflow = {};

  // Aggregate calls, distributions, and calculate net cash flow data from all companies
  Object.values(cfs).forEach((company) => {
    Object.entries(company.calls).forEach(([key, value]) => {
      if (key.startsWith("Dec")) {
        aggregatedCalls[key] = (aggregatedCalls[key] || 0) + value;
      }
    });

    Object.entries(company.distributions).forEach(([key, value]) => {
      if (key.startsWith("Dec")) {
        aggregatedDistributions[key] =
          (aggregatedDistributions[key] || 0) + value;
      }
    });
  });

  // Calculate net cash flow for each "Dec" point
  Object.keys({ ...aggregatedCalls, ...aggregatedDistributions }).forEach(
    (key) => {
      if (key.startsWith("Dec")) {
        aggregatedNetCashflow[key] =
          (aggregatedDistributions[key] || 0) + (aggregatedCalls[key] || 0);
      }
    }
  );

  const summedNetCashflow = Object.values(aggregatedNetCashflow).reduce(
    (sum, value) => sum + value,
    0
  );
  const summedCalls = Object.values(aggregatedCalls).reduce(
    (sum, value) => sum + value,
    0
  );
  const rawNetEM = Math.abs(summedNetCashflow / summedCalls) + 1 || 0;
  const netEM = rawNetEM.toFixed(4);
  const targetExit = Math.abs(summedCalls) * rawNetEM;
  return {
    aggregatedCalls,
    aggregatedDistributions,
    aggregatedNetCashflow,
    netEM,
    targetExit,
  };
}

export function calculateCapital(cfs, investments) {
  const deals = Object.keys(cfs);
  const fundedCapital = deals.reduce((acc, deal) => {
    const dealInvestment = investments.find(
      (investment) => investment.name === deal
    );
    const dealCapital =
      cfs[deal].calls["Funded To Date"] * dealInvestment.allocationUSD;
    return acc + dealCapital;
  }, 0);
  const totalInvestment = investments.reduce((acc, investment) => {
    return acc + investment.allocationUSD;
  }, 0);
  return {
    fundedCapital,
    unfundedCapital: totalInvestment - fundedCapital,
    totalCapital: totalInvestment,
  };
}

export function getCallsData(cfs) {
  const { aggregatedCalls, aggregatedDistributions } =
    aggregateCallsAndDistributions(cfs);

  // only keep the years that have values
  Object.keys(aggregatedCalls).forEach((key) => {
    if (aggregatedCalls[key] === 0 && aggregatedDistributions[key] === 0) {
      delete aggregatedCalls[key];
      delete aggregatedDistributions[key];
    }
  });
  ensureNoSkippedYears(aggregatedCalls, aggregatedDistributions);
  // Generate years dynamically based on the keys
  const years = Object.keys(aggregatedCalls)
    .concat(Object.keys(aggregatedDistributions))
    .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
    .map((key) => key.replace("Dec ", "31.12.20"))
    .sort();

  const capitalCallData = years.map(
    (year) => aggregatedCalls[year.replace("31.12.20", "Dec ")] || 0
  );
  const capitalDistributionData = years.map(
    (year) => aggregatedDistributions[year.replace("31.12.20", "Dec ")] || 0
  );
  const cumulativeCFsData = capitalCallData
    .map((call, index) => call + (capitalDistributionData[index] || 0))
    .map((val, i, arr) =>
      arr.slice(0, i + 1).reduce((acc, curr) => acc + curr, 0)
    );
  const totalCapitalCalls = Math.abs(
    capitalCallData.reduce((sum, value) => sum + Math.round(value), 0)
  );
  return {
    years,
    capitalCallData,
    capitalDistributionData,
    cumulativeCFsData,
    totalCapitalCalls,
  };
}

export function customNumberFormat(number) {
  if (number === 0) return "-"; // Return dash for zero
  const formatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
  const formattedNumber = new Intl.NumberFormat("en-US", formatOptions).format(
    Math.abs(number)
  );
  return number < 0 ? `(${formattedNumber})` : formattedNumber;
}

function ensureNoSkippedYears(aggregatedCalls, aggregatedDistributions) {
  // Helper to extract the numeric part of the "Dec XX" format
  const extractYear = (key) => parseInt(key.split(" ")[1], 10);
  // Inverse of extractYear, to reformat numbers back to "Dec XX" format
  const formatYear = (year) => `Dec ${year}`;

  const allYears = new Set([
    ...Object.keys(aggregatedCalls).map(extractYear),
    ...Object.keys(aggregatedDistributions).map(extractYear),
  ]);
  const years = Array.from(allYears).sort((a, b) => a - b);
  const minYear = years[0];
  const maxYear = years[years.length - 1];

  // Fill missing years with zeros
  for (let year = minYear; year <= maxYear; year++) {
    const yearKey = formatYear(year);
    if (!aggregatedCalls.hasOwnProperty(yearKey)) {
      aggregatedCalls[yearKey] = 0;
    }
    if (!aggregatedDistributions.hasOwnProperty(yearKey)) {
      aggregatedDistributions[yearKey] = 0;
    }
  }
}
